import React, { useState, useEffect, useCallback } from 'react';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import { getStripe, resetStripeCache } from '../config/stripe';

export function formatStripeError(err) {
  if (!err) return 'Something went wrong';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  return 'Something went wrong';
}

const stripeFieldStyle = {
  style: {
    base: {
      color: '#ffffff',
      fontSize: '16px',
      fontFamily: 'system-ui, sans-serif',
      lineHeight: '24px',
      '::placeholder': {
        color: '#64748b',
      },
    },
    invalid: {
      color: '#f87171',
    },
  },
};

function StripeField({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-white/50 uppercase tracking-wider">
        {label}
      </label>
      <div className="rounded-xl border border-dark-border bg-dark-bg/60 px-4 py-3.5 min-h-[48px] flex items-center">
        {children}
      </div>
    </div>
  );
}

function AddCardForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldsReady, setFieldsReady] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });

  const allFieldsReady = fieldsReady.number && fieldsReady.expiry && fieldsReady.cvc;

  const handleFieldChange = (event) => {
    if (event.error) {
      setError(event.error.message);
    } else if (error) {
      setError(null);
    }
  };

  const markReady = (field) => {
    setFieldsReady((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) {
      setError('Payment form is still loading. Wait a moment and try again.');
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setError('Card form is not ready. Refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumber,
          },
        }
      );

      if (confirmError) {
        setError(formatStripeError(confirmError));
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      if (setupIntent?.status === 'succeeded' && paymentMethodId) {
        await onSuccess(paymentMethodId);
        return;
      }

      setError('Could not complete card setup. Please try again.');
    } catch (err) {
      setError(formatStripeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <StripeField label="Card number">
        <CardNumberElement
          className="w-full"
          options={stripeFieldStyle}
          onReady={() => markReady('number')}
          onChange={handleFieldChange}
        />
      </StripeField>

      <div className="grid grid-cols-2 gap-4">
        <StripeField label="Expiry date">
          <CardExpiryElement
            className="w-full"
            options={stripeFieldStyle}
            onReady={() => markReady('expiry')}
            onChange={handleFieldChange}
          />
        </StripeField>
        <StripeField label="Security code">
          <CardCvcElement
            className="w-full"
            options={stripeFieldStyle}
            onReady={() => markReady('cvc')}
            onChange={handleFieldChange}
          />
        </StripeField>
      </div>

      {!allFieldsReady && (
        <p className="text-white/50 text-sm">Loading secure card form...</p>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 py-3 rounded-xl font-medium bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || !allFieldsReady || submitting}
          className="flex-1 py-3 rounded-xl font-semibold bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save card'}
        </button>
      </div>
    </form>
  );
}

function AddCardModal({
  isOpen,
  onClose,
  onSuccess,
  title = 'Add a card',
  description = 'Save a card to your account for quick checkout.',
}) {
  const { token } = useAuth();
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetState = useCallback(() => {
    setClientSecret(null);
    setStripeInstance(null);
    setError(null);
    setLoading(false);
  }, []);

  const saveCardToAccount = async (paymentMethodId) => {
    const response = await fetch(`${API_URL}/stripe/payment-methods/default`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ paymentMethodId }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Failed to save card on your account');
    }
  };

  useEffect(() => {
    if (!isOpen || !token) {
      return undefined;
    }

    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      setClientSecret(null);
      setStripeInstance(null);

      try {
        resetStripeCache();
        const stripe = await getStripe(token);
        if (!stripe) {
          throw new Error(
            'Payments are not set up. Add your Stripe publishable key to backend/.env from the Stripe dashboard.'
          );
        }

        const response = await fetch(`${API_URL}/stripe/setup-intent`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to start card setup');
        }

        if (!data.clientSecret) {
          throw new Error('Invalid setup response from server');
        }

        if (!cancelled) {
          setStripeInstance(stripe);
          setClientSecret(data.clientSecret);
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatStripeError(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [isOpen, token]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleCardSaved = async (paymentMethodId) => {
    await saveCardToAccount(paymentMethodId);
    resetState();
    if (onSuccess) {
      await onSuccess();
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-5"
      onClick={handleClose}
    >
      <div
        className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm md:text-base mb-4">{description}</p>

        <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">
          <svg
            className="w-3.5 h-3.5 text-white/40 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <span className="text-white/40 text-xs">Secured by Stripe</span>
        </div>

        {loading && (
          <p className="text-white/50 text-sm py-8 text-center">Preparing secure form...</p>
        )}

        {error && !clientSecret && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!loading && clientSecret && stripeInstance && (
          <Elements
            key={clientSecret}
            stripe={stripeInstance}
            options={{
              clientSecret,
              appearance: { theme: 'night' },
            }}
          >
            <AddCardForm
              clientSecret={clientSecret}
              onSuccess={handleCardSaved}
              onCancel={handleClose}
            />
          </Elements>
        )}

        {!loading && error && clientSecret && (
          <button
            type="button"
            onClick={handleClose}
            className="mt-4 w-full py-3 rounded-xl font-medium bg-dark-surface/50 border border-dark-border text-white"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

export default AddCardModal;
